/**
 * Contact Select Address — 후보 표시·폼 매핑 (비교/추천 없음).
 */
import { joinAddressDisplayParts, formatCountryForAddressDisplay } from './addressJoinParts';
import { formatMyAddressRowBodyForDisplay, shouldShowMyLocationFields } from './contactMyLocationFields';
import type { ContactAddressCandidate, ContactAddressTypeCode } from '../types/contact.types';

const UUID_LIKE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const PLACEHOLDER = /^(NA|N\/A|N\.A\.|NONE|null|-)$/i;

function isGuidLike(value: unknown): boolean {
    return UUID_LIKE.test(String(value ?? '').trim());
}

function isNumericPostcodeLike(value: unknown): boolean {
    return /^\d{4,6}$/.test(String(value ?? '').trim());
}

function sanitizeLocationDisplayPart(value: unknown): string {
    const s = String(value ?? '').trim();
    if (!s || isGuidLike(s) || PLACEHOLDER.test(s)) return '';
    return s;
}

/** MY 수동 등록 등 — postcode/state 필드가 뒤바뀐 경우 표시용 보정 */
function normalizeCandidateAddressForDisplay(addr: Record<string, unknown>): Record<string, unknown> {
    const a = { ...addr };
    const postcodeBinding = a.postcode ?? a.post_code ?? a.postalCode ?? a.postcodeCode;
    const stateVal = a.state ?? a.stateCode;

    if (isGuidLike(postcodeBinding) && isNumericPostcodeLike(stateVal)) {
        a.postcode = stateVal;
        a.post_code = stateVal;
        if (isGuidLike(a.state) || isNumericPostcodeLike(a.state)) {
            a.state = sanitizeLocationDisplayPart(a.stateCode);
        }
    }

    for (const key of ['country', 'state', 'city', 'postcode', 'post_code', 'postalCode'] as const) {
        if (isGuidLike(a[key])) {
            a[key] = '';
        }
    }

    return a;
}

function formatCandidateDisplayLineFallback(addr: Record<string, unknown>): string {
    const countryRaw = addr.country ?? addr.countryCode;
    const countryDisplay = formatCountryForAddressDisplay(
        sanitizeLocationDisplayPart(countryRaw),
    );
    const streets = [
        addr.street01 ?? addr.street1 ?? addr.address1 ?? addr.addressLine1,
        addr.street02 ?? addr.street2 ?? addr.address2 ?? addr.addressLine2,
        addr.street03 ?? addr.street3 ?? addr.address3 ?? addr.addressLine3,
    ];
    if (!shouldShowMyLocationFields(countryRaw)) {
        return joinAddressDisplayParts([...streets, countryDisplay]);
    }
    return joinAddressDisplayParts([
        ...streets,
        sanitizeLocationDisplayPart(addr.postcode ?? addr.post_code ?? addr.zip ?? addr.postalCode),
        sanitizeLocationDisplayPart(addr.city),
        sanitizeLocationDisplayPart(addr.state ?? addr.stateCode),
        countryDisplay,
    ]);
}

export const CONTACT_ADDR_TYPE_LABEL: Record<ContactAddressTypeCode, string> = {
    M04001: 'Residential',
    M04002: 'Service',
    M04003: 'Business',
};

export interface MyAddressFormShape {
    addressLine1: string;
    addressLine2: string;
    addressLine3: string;
    country: string;
    state: string;
    city: string;
    postcode: string;
}

export function formatCandidateDisplayLine(addr: Record<string, unknown> | null | undefined): string {
    if (!addr || typeof addr !== 'object') return '(empty)';
    const normalized = normalizeCandidateAddressForDisplay(addr);
    const line =
        formatMyAddressRowBodyForDisplay(normalized) ||
        formatCandidateDisplayLineFallback(normalized);
    return line || '(empty)';
}

export function getCandidateDisplayLine(candidate: ContactAddressCandidate): string {
    if (candidate.address && typeof candidate.address === 'object') {
        return formatCandidateDisplayLine(candidate.address);
    }
    return String(candidate.displayLine ?? '').trim() || '(empty)';
}

export function mapCandidateToMyAddressData(
    addr: Record<string, unknown> | null | undefined
): MyAddressFormShape {
    const a = addr || {};
    return {
        addressLine1: String(a.street01 ?? a.street1 ?? a.address1 ?? a.addressLine1 ?? '').trim(),
        addressLine2: String(a.street02 ?? a.street2 ?? a.address2 ?? a.addressLine2 ?? '').trim(),
        addressLine3: String(a.street03 ?? a.street3 ?? a.address3 ?? a.addressLine3 ?? '').trim(),
        country: String(a.country ?? '').trim(),
        state: String(a.state ?? '').trim(),
        city: String(a.city ?? '').trim(),
        postcode: String(a.post_code ?? a.postcode ?? a.postalCode ?? '').trim(),
    };
}

export function filterCandidatesBySearch(
    candidates: ContactAddressCandidate[],
    query: string
): ContactAddressCandidate[] {
    const q = String(query || '').trim().toLowerCase();
    if (!q) return candidates;
    return candidates.filter((c) => {
        const hay = [
            getCandidateDisplayLine(c),
            c.addressTypeLabel,
            c.sourceSystem,
            c.meta?.position,
            c.meta?.entityCd,
        ]
            .map((v) => String(v ?? '').toLowerCase())
            .join(' ');
        return hay.includes(q);
    });
}

export function filterCandidatesByAddressType(
    candidates: ContactAddressCandidate[],
    addressType: ContactAddressTypeCode | null | undefined
): ContactAddressCandidate[] {
    if (!addressType) return candidates;
    return candidates.filter((c) => c.addressType === addressType);
}
